import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { cva } from 'class-variance-authority';
import { Button, Flex, Icon, Input, Text, useKeyboardSelectableList } from '@nula/core-ui';
import {
  templateAvailability,
  type DocumentSourceFacts,
  type TemplateSummary,
} from '@/lib/templates/catalog';
import { getEntityKindMeta } from '@/utils/entity-kind-meta';
import { TemplateOwnerTag } from './template-owner-tag';
import styles from './surface-templates-face.module.css';

interface SurfaceTemplatesFaceProps {
  templates: readonly TemplateSummary[];
  facts: DocumentSourceFacts;
  query: string;
  onQueryChange: (query: string) => void;
  onPick: (templateId: string) => void;
  onBack: () => void;
  /** The row to highlight on open instead of the best suggestion. */
  initialHighlightKey?: string | null;
  /** Reports the picked row's key, so the face can reopen on that row. */
  onPickRow?: (rowKey: string) => void;
}

interface Entry {
  template: TemplateSummary;
  reason: string | null;
}

interface Row extends Entry {
  key: string;
}

interface Group {
  label: string | null;
  rows: Row[];
}

type TypeFilter = 'all' | TemplateSummary['outputType'];

const SINGLE_LIST_LIMIT = 8;
const TYPE_FILTER_KEY = 'nula:templates:type-filter';
const RECENT_LIMIT = 3;
const LIST_KEYS = new Set(['ArrowUp', 'ArrowDown', 'Home', 'End', 'Enter']);

const back = cva(styles.back);
const option = cva(styles.option, {
  variants: {
    highlighted: { true: styles.highlighted },
    unavailable: { true: styles.unavailable },
  },
});

const byName = (a: Entry, b: Entry) => a.template.name.localeCompare(b.template.name);

function entriesOf(templates: readonly TemplateSummary[], facts: DocumentSourceFacts): Entry[] {
  return templates.map((template) => {
    const availability = templateAvailability(template, facts);
    return { template, reason: availability.available ? null : availability.reason };
  });
}

function rowsFor(group: string, entries: Entry[]): Row[] {
  return entries.map((entry) => ({ ...entry, key: `${group}:${entry.template.id}` }));
}

/** Every list reads top-down with its best entry first. */
function browseGroups(templates: readonly TemplateSummary[], facts: DocumentSourceFacts): Group[] {
  const entries = entriesOf(templates, facts);
  const available = entries.filter((entry) => entry.reason === null);
  const used = available
    .filter((entry) => entry.template.lastUsedAt !== null)
    .sort((a, b) => (b.template.lastUsedAt ?? '').localeCompare(a.template.lastUsedAt ?? ''));

  if (templates.length <= SINGLE_LIST_LIMIT) {
    const latest = used.slice(0, 1);
    const unavailable = entries.filter((entry) => entry.reason !== null).sort(byName);
    const rest = available.filter((entry) => !latest.includes(entry)).sort(byName);
    return [{ label: null, rows: rowsFor('all', [...latest, ...rest, ...unavailable]) }];
  }

  const recent = used.slice(0, RECENT_LIMIT);
  const groups: Group[] = recent.length > 0 ? [{ label: 'Recent', rows: rowsFor('recent', recent) }] : [];
  groups.push({
    label: `All · ${templates.length}`,
    rows: rowsFor('all', entries.filter((entry) => !recent.includes(entry)).sort(byName)),
  });
  return groups;
}

function matchRows(
  templates: readonly TemplateSummary[],
  facts: DocumentSourceFacts,
  query: string,
): Row[] {
  const needle = query.trim().toLowerCase();
  const entries = entriesOf(templates, facts);
  const nameMatches = entries.filter((e) => e.template.name.toLowerCase().includes(needle));
  const descriptionMatches = entries.filter(
    (e) => !nameMatches.includes(e) && e.template.description.toLowerCase().includes(needle),
  );
  return rowsFor('match', [...nameMatches.sort(byName), ...descriptionMatches.sort(byName)]);
}

function nameWithMatches(name: string, query: string): ReactNode {
  const needle = query.trim().toLowerCase();
  if (needle === '') return name;
  const parts: ReactNode[] = [];
  const haystack = name.toLowerCase();
  let from = 0;
  for (let at = haystack.indexOf(needle); at !== -1; at = haystack.indexOf(needle, from)) {
    if (at > from) parts.push(name.slice(from, at));
    parts.push(<Text key={at} strong>{name.slice(at, at + needle.length)}</Text>);
    from = at + needle.length;
  }
  if (from < name.length) parts.push(name.slice(from));
  return parts;
}

function readTypeFilter(): TypeFilter {
  try {
    const stored = localStorage.getItem(TYPE_FILTER_KEY);
    return stored === 'text' || stored === 'canvas' ? stored : 'all';
  } catch {
    return 'all';
  }
}

function writeTypeFilter(type: TypeFilter): void {
  try {
    localStorage.setItem(TYPE_FILTER_KEY, type);
  } catch {
    // The choice just isn't remembered.
  }
}

const TYPE_OPTIONS: { value: TypeFilter; label: string; icon?: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'text', label: 'Document', icon: getEntityKindMeta('document').icon },
  { value: 'canvas', label: 'Canvas', icon: getEntityKindMeta('canvas').icon },
];

const typeButton = cva(styles.type, { variants: { on: { true: styles.typeOn } } });

function bestIndexOf(rows: Row[]): number {
  return Math.max(rows.findIndex((row) => row.reason === null), 0);
}

export function SurfaceTemplatesFace({
  templates, facts, query, onQueryChange, onPick, onBack, initialHighlightKey = null, onPickRow,
}: SurfaceTemplatesFaceProps) {
  const baseId = useId();
  const listId = `${baseId}-list`;
  const searching = query.trim() !== '';
  const fieldRef = useRef<HTMLInputElement>(null);

  const [chosenType, setChosenType] = useState<TypeFilter>(readTypeFilter);
  const kinds = new Set(templates.map((template) => template.outputType));
  const offerTypes = kinds.size > 1;
  const type: TypeFilter = offerTypes && (chosenType === 'all' || kinds.has(chosenType)) ? chosenType : 'all';
  const shown = useMemo(
    () => (type === 'all' ? templates : templates.filter((template) => template.outputType === type)),
    [templates, type],
  );
  const chooseType = (next: TypeFilter) => {
    setChosenType(next);
    writeTypeFilter(next);
    fieldRef.current?.focus();
  };

  const groups = useMemo<Group[]>(
    () => (searching
      ? [{ label: null, rows: matchRows(shown, facts, query) }]
      : browseGroups(shown, facts)),
    [searching, shown, facts, query],
  );
  const rows = useMemo(() => groups.flatMap((group) => group.rows), [groups]);
  const indexByKey = useMemo(() => new Map(rows.map((row, i) => [row.key, i])), [rows]);
  const bestIndex = bestIndexOf(rows);

  const { highlightedIndex, setHighlightedIndex, getItemProps, getListProps } =
    useKeyboardSelectableList({
      items: rows,
      initialIndex: indexByKey.get(initialHighlightKey ?? '') ?? bestIndex,
      onSelect: (row) => {
        if (row.reason !== null) return;
        onPickRow?.(row.key);
        onPick(row.template.id);
      },
    });

  // Reset during render, not in an effect, so aria-activedescendant never
  // names an option from the previous list for a frame.
  const listFor = `${type}:${query}`;
  const [highlightedFor, setHighlightedFor] = useState(listFor);
  if (highlightedFor !== listFor || (rows.length > 0 && highlightedIndex >= rows.length)) {
    setHighlightedFor(listFor);
    setHighlightedIndex(bestIndex);
  }

  const optionId = (index: number) => `${baseId}-option-${index}`;
  const highlightedId = rows.length > 0 ? optionId(highlightedIndex) : undefined;

  useEffect(() => {
    if (highlightedId) document.getElementById(highlightedId)?.scrollIntoView?.({ block: 'nearest' });
  }, [highlightedId]);

  const { onKeyDown: onListKeyDown } = getListProps();

  // Focus never leaves the field, so every key it handles has to stop here:
  // the panel's roving-focus handler would otherwise also act on it.
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      if (query !== '') onQueryChange('');
      else onBack();
      return;
    }
    if (!LIST_KEYS.has(e.key)) return;
    e.stopPropagation();
    onListKeyDown(e);
  };

  const renderRow = (row: Row) => {
    const i = indexByKey.get(row.key) ?? -1;
    const highlighted = i === highlightedIndex;
    const unavailable = row.reason !== null;
    const kind = row.template.outputType === 'canvas' ? 'canvas' : 'document';
    return (
      <Flex
        key={row.key}
        id={optionId(i)}
        align="center"
        {...getItemProps(i)}
        aria-label={unavailable ? `${row.template.name}, ${row.reason}` : row.template.name}
        aria-disabled={unavailable || undefined}
        className={option({ highlighted, unavailable })}
      >
        <Icon name={getEntityKindMeta(kind).icon} size={16} className={styles.kindIcon} />
        <Text className={styles.name}>{nameWithMatches(row.template.name, query)}</Text>
        {unavailable && <Text className={styles.reason}>{row.reason}</Text>}
        {highlighted && !unavailable
          ? <Text className={styles.enter}>↵</Text>
          : <TemplateOwnerTag owner={row.template.owner} dimmed={unavailable} className={styles.trailing} />}
      </Flex>
    );
  };

  return (
    <Flex vertical className={styles.templatesFace}>
      <button
        type="button"
        role="menuitem"
        aria-label="Back to New"
        onClick={onBack}
        className={back()}
      >
        <Flex align="center" justify="center" className={styles.backIcon} aria-hidden="true">
          <Icon name="arrow_back" size={16} />
        </Flex>
        <Text className={styles.title}>Templates</Text>
      </button>

      {offerTypes && (
        <Flex role="group" aria-label="Template type" align="center" className={styles.types}>
          {TYPE_OPTIONS.map((option) => (
            <Button
              key={option.value}
              size="sm"
              pill
              aria-pressed={option.value === type}
              icon={option.icon ? <Icon name={option.icon} size={14} /> : undefined}
              onClick={() => chooseType(option.value)}
              className={typeButton({ on: option.value === type })}
            >
              {option.label}
            </Button>
          ))}
        </Flex>
      )}

      <Flex vertical className={styles.scroll}>
        <Flex vertical className={styles.stack}>
          {searching && rows.length > 0 && (
            <Text type="secondary" className={styles.label}>{`${rows.length} of ${shown.length}`}</Text>
          )}
          {searching && rows.length === 0 && (
            <Text type="secondary" className={styles.empty}>{`No templates match "${query}"`}</Text>
          )}
          <Flex vertical id={listId} role="listbox" aria-label="Templates">
            {groups.map((group) => (group.label === null
              ? group.rows.map(renderRow)
              : (
                <Flex vertical role="group" aria-label={group.label} key={group.label}>
                  <Text className={styles.label} aria-hidden="true">{group.label}</Text>
                  {group.rows.map(renderRow)}
                </Flex>
              )))}
          </Flex>
        </Flex>
      </Flex>

      <Input
        ref={fieldRef}
        autoFocus
        role="combobox"
        aria-label="Search templates"
        aria-expanded
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={highlightedId}
        placeholder={type === 'all'
          ? `Search ${templates.length} templates`
          : `Search ${shown.length} ${type === 'canvas' ? 'canvas' : 'document'} templates`}
        prefix={<Icon name="search" size={16} />}
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        onKeyDown={handleKeyDown}
        className={styles.search}
      />
    </Flex>
  );
}
